import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createReferee, getRefereeById, updateReferee } from '../../services/refereeService';
import { getDepartments } from '../../services/departmentService';
import { Department } from '../../types/models';
import { ArrowLeft, Save } from 'lucide-react';

interface RefereeFormData {
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  email: string;
  phone?: string;
  is_active: boolean;
  department_id?: string;
}

function RefereeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [formData, setFormData] = useState<RefereeFormData>({
    first_name: '',
    last_name: '',
    first_name_tamil: '',
    last_name_tamil: '',
    email: '',
    phone: '',
    is_active: true,
    department_id: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const departmentsData = await getDepartments();
        setDepartments(departmentsData);
      } catch (err) {
        console.error('Error loading departments:', err);
        setError('Failed to load departments');
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (id) {
      loadReferee();
    }
  }, [id]);

  const loadReferee = async () => {
    try {
      setLoading(true);
      const data = await getRefereeById(id!);
      setFormData(data);
      setError(null);
    } catch (err) {
      setError('Failed to load referee');
      console.error('Error loading referee:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (id) {
        await updateReferee(id, formData);
      } else {
        await createReferee(formData);
      }
      navigate('/referees');
    } catch (err) {
      setError('Failed to save referee');
      console.error('Error saving referee:', err);
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {id ? t('referees.form.editTitle') : t('referees.form.addTitle')}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.firstName.label')}
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                placeholder={t('referees.form.fields.firstName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.lastName.label')}
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                placeholder={t('referees.form.fields.lastName.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="first_name_tamil" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.firstNameTamil.label')}
              </label>
              <input
                type="text"
                id="first_name_tamil"
                name="first_name_tamil"
                value={formData.first_name_tamil}
                onChange={handleChange}
                placeholder={t('referees.form.fields.firstNameTamil.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="last_name_tamil" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.lastNameTamil.label')}
              </label>
              <input
                type="text"
                id="last_name_tamil"
                name="last_name_tamil"
                value={formData.last_name_tamil}
                onChange={handleChange}
                placeholder={t('referees.form.fields.lastNameTamil.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.email.label')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={t('referees.form.fields.email.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.phone.label')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t('referees.form.fields.phone.placeholder')}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="department_id" className="block text-sm font-medium text-gray-700">
                {t('referees.form.fields.department.label')}
              </label>
              <select
                id="department_id"
                name="department_id"
                value={formData.department_id}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">{t('referees.form.fields.department.placeholder')}</option>
                {departments.map(department => (
                  <option key={department.department_id} value={department.department_id}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="form-checkbox h-5 w-5 text-blue-600"
                />
                <span className="ml-2 text-gray-700">{t('referees.form.fields.isActive.label')}</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={() => navigate('/referees')}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors"
            >
              {t('referees.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 inline-flex items-center"
            >
              <Save className="h-4 w-4 mr-2" />
              {loading ? t('referees.actions.saving') : t('referees.actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RefereeForm;